/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { task, timeout } from 'ember-concurrency';
import { isThenable } from 'volta/utils/object-utils';

import { debug } from '@ember/debug';
import { action } from '@ember/object';
import Component from '@glimmer/component';

import type { BvTooltipSignature } from './../bv-tooltip/index';
import type { TSize } from 'volta/utils/style-utils';
import type { TText } from 'volta/utils/text-utils';
import type { TTooltipSide } from '../bv-tooltip';
import type { BvIconSignature } from '../bv-icon';
import type { ContentValue } from '@glint/template';

export type TButtonSize = Extract<TSize, 'xs' | 'sm'>;

const Sizes: Record<TButtonSize, TButtonSize> = {
  sm: 'sm',
  xs: 'xs'
};

export type IBtnColor = 'primary' | 'danger' | 'hud';

const Colors: Record<IBtnColor, IBtnColor> = {
  primary: 'primary',
  danger: 'danger',
  hud: 'hud'
};

export type IAlignment = 'left' | 'right';

const Alignments: Record<IAlignment, IAlignment> = {
  left: 'left',
  right: 'right'
};

export interface BvBtnArgs {
  color?: IBtnColor;
  size?: TButtonSize;
  type?: 'submit' | 'button';
  alignText?: IAlignment;
  iconLeft?: string;
  iconLeftColor?: BvIconSignature['Args']['color'];
  iconRight?: string;
  iconRightColor?: BvIconSignature['Args']['color'];
  text?: TText;
  content?: ContentValue; // deprecated
  disclosure?: 'auto' | 'up' | 'down' | boolean;
  submit?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  active?: boolean;
  pressed?: boolean;
  outline?: boolean;
  rounded?: boolean;
  subtle?: boolean;
  plain?: boolean;
  fullWidth?: boolean;
  class?: string;
  testName?: string;
  tooltipText?: BvTooltipSignature['Args']['text'];
  tooltipSide?: TTooltipSide;
  loadingProgress?: number;
  route?: string;
  routeModel?: unknown;
  routeModels?: unknown[];
  routeQuery?: object;
  tooltip?: BvTooltipSignature['Args']['content'] | TText;
  accessibilityLabel?: string;
  onClick?: (event: Event) => void | Promise<any> | any;
  onFocus?: (event: Event) => void;
  onBlur?: (event: Event) => void;
}

export interface BvBtnSignature {
  Args: BvBtnArgs;
  Blocks: {
    default: [];
  };
  Element: HTMLButtonElement | HTMLElement;
}

const BvBtnColors = new Set([Colors.primary, Colors.danger, Colors.hud]);
const BvBtnSizes = new Set([Sizes.sm, Sizes.xs]);
const BvBtnAlignments = new Set([Alignments.left, Alignments.right]);

/**
 * Timeout used in async action to simulated a minimum loading time in milliseconds
 */
const DefaultTaskTimeout: number = 300;

export default class BvBtnComponent extends Component<BvBtnSignature> {
  promise?: Promise<any>;

  /**
   * Button color class name
   */
  get colorClassName() {
    const { color } = this.args;
    return color && BvBtnColors.has(color) ? `bv-btn--${color}` : undefined;
  }

  /**
   * Button size class modifier
   */
  get sizeClassName() {
    const { size } = this.args;
    return size && BvBtnSizes.has(size) ? `bv-btn--${size}` : undefined;
  }

  /**
   * BvBtn alignment class modifier
   */
  get alignClassName() {
    const { alignText } = this.args;
    return alignText && BvBtnAlignments.has(alignText as IAlignment)
      ? `bv-btn--align-${alignText}`
      : undefined;
  }

  /**
   * BvBtn has only one icon defined in its args
   * true if one icon only is defined and no text
   */
  get hasOnlyOneIcon() {
    const { iconLeft, iconRight, text, disclosure } = this.args;
    return (
      !text &&
      ((Boolean(iconLeft) && !iconRight && !disclosure) ||
        (!iconLeft && (Boolean(iconRight) || Boolean(disclosure))))
    );
  }

  @action
  handleRouteClick(event: MouseEvent): boolean | { event: MouseEvent } {
    if (this.args.disabled) {
      return false;
    }
    event.stopPropagation();
    return this.handleClick(event);
  }

  /**
   * BvBtn click event handler. If the passed onClick function returns a promise
   * the asyncAction will be performed
   * @param event HTML element click event
   * @return {boolean|*}
   */
  @action
  handleClick(event: MouseEvent): boolean | { event: MouseEvent } {
    const { submit, onClick, disabled } = this.args;

    if (disabled) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }

    if (submit) {
      return { event };
    }

    const maybePromise: Promise<any> | undefined = onClick?.(event);

    if (maybePromise && isThenable(maybePromise)) {
      this.promise = maybePromise;
      this.asyncAction.perform(maybePromise);
    }
    return false;
  }

  /**
   * Asynchronous action performed when the passed onClick argument returns a promise
   * @param promise A promise returned by an async function passed in the onClick argument
   */
  asyncAction = task(async (promise?: Promise<any>) => {
    try {
      if (!promise) {
        return;
      }
      await timeout(DefaultTaskTimeout);
      return await promise;
    } catch (error) {
      debug(error as string);
    }
  });
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvBtn: typeof BvBtnComponent;
    'bv-btn': typeof BvBtnComponent;
  }
}
